import React, { useEffect, useRef, useState } from "react";
import Styles from "./EditMessageArea.module.scss"
import { EmojiClickData } from "emoji-picker-react";
import Boschicon from "../boschIcon/Boschicon";
import ModalGetEmoji from "../../components/modals/ModalGetEmoji";
import ConfirmCancelDialog from "../../components/modals/ConfirmCancelDialog";
import { useTranslation } from "react-i18next";
import Attachment from "../../containers/InputChat/Attachment";

interface EditMessageAreaProps {
    message: any;
    hasAttachment: boolean;
    attachmentIsImage: boolean;
    metadata: any;
    userId: string;
    createdTimestamp: string | null;
    onSave: (m: any, userInput: string) => void;
    onDeleteMessage: (m: any) => void;
}

const EditMessageArea: React.FC<EditMessageAreaProps> = React.memo(
    ({ message, hasAttachment, metadata, attachmentIsImage, userId, createdTimestamp, onSave, onDeleteMessage }) => {
        const { t } = useTranslation();
        const [userInput, setUserInput] = useState<string>(message ? message.content : "");
        const inputRef = useRef<HTMLTextAreaElement>(null);
        const maxSizeMessageInWord = 4000;
        const [countWordMesssage, setCountWordMesssage] = useState<number>(0);
        const [openEdit, setOpenEdit] = useState<boolean>(false);
        const [openCancelConfirmModal, setOpenCancelConfirmModal] = useState<boolean>(false);
        const [openEmojiModal, setOpenEmojiModal] = useState<boolean>(false);


        const updateInputHeight = () => {
            if (inputRef.current) {
                let textarea = inputRef.current;
                textarea.style.height = 'auto'; // Reset the height to auto to measure scrollHeight
                textarea.style.height = `${textarea.scrollHeight}px`; // Set the height based on content
            }
        }

        const userInputChangeHandler = (e: any) => {
            let text = e.target.value || "";
            let countWord = text.length;
            if (countWord <= maxSizeMessageInWord) {
                setUserInput(text);
                setCountWordMesssage(countWord)
            }

            if (countWord > maxSizeMessageInWord) {
                let maxSizeMessageInWordInText = text.substring(0, maxSizeMessageInWord);
                setUserInput(maxSizeMessageInWordInText);
                setCountWordMesssage(maxSizeMessageInWordInText.length)
            }

            updateInputHeight();
        };

        const insertLineBreak = () => {
            const textarea = inputRef.current;
            if (!textarea) {
                return;
            }
            const { selectionStart, selectionEnd } = textarea;
            const value = textarea.value;
            const newText = `${value.substring(0, selectionStart)}\n${value.substring(selectionEnd)}`;

            setUserInput(newText);

            const newPosition = selectionStart + 1;
            textarea.setSelectionRange(newPosition, newPosition);
            textarea.style.height = 'auto'; // Reset the height to auto to measure scrollHeight
            textarea.style.height = `${textarea.scrollHeight}px`; // Set the height based on content
        };

        const resize = (size: string) => {
            let size_num = Number(size);

            if (size_num < 1024) return `${size} bytes`;

            return size_num < 1024 * 1024
                ? `${String((size_num / 1024).toFixed(2))} KB`
                : `${String((size_num / 1024 / 1024).toFixed(2))} MB`;
        };
        const onHandleSave = (message: any) => {
            if (userInput && userInput.trim() != "") {
                onSave(message, userInput);
                setUserInput("");
                setOpenEdit(false);
            }
        };

        const onCancelChange = () => {
            setOpenCancelConfirmModal(true)
        };

        const handleOnDiscard = () => {
            setUserInput(message.content);
            setOpenEdit(false)
            setOpenCancelConfirmModal(false)
        };

        const getEmoji = (data: EmojiClickData) => {
            setUserInput(userInput + data.emoji)
            setOpenEmojiModal(false)
        }


        useEffect(() => {
            updateInputHeight();
        }, [inputRef.current, message])

        useEffect(() => {
            setCountWordMesssage(message.content.length || 0);
            setUserInput(message.content);
        }, [message])

        return (
            <>
                <div className={`${Styles["messages-receiver"]}`}>
                    <div className={Styles["ChatBubbleContainer"]}>
                        {
                            !openEdit && (
                                <div className={Styles["ChatBubbleContainer-content"]}>
                                    <div className={Styles["ChatBubbleContainer-actions"]}>
                                        <div className={Styles["ChatBubbleContainer-actions-icon"]}>
                                            <Boschicon name="bosch-ic-edit" onClick={() => setOpenEdit(true)} />
                                        </div>
                                        <div className={`${Styles["ChatBubbleContainer-actions-icon"]} ${Styles["delete"]}`}>
                                            <Boschicon name="bosch-delete" onClick={() => onDeleteMessage(message)} />
                                        </div>

                                    </div>
                                    <div className={`${Styles["content-main-area"]}`}>
                                        <div className={`${Styles["ChatBubble"]} --major-accent__enabled__fill__default`}>
                                            <div className={Styles["ChatBubble-content"]}>
                                                {hasAttachment && (
                                                    <div className={Styles["attachment-message"]}>
                                                        <Attachment
                                                            fileKey={metadata?.attachment?.fileKey}
                                                            name={metadata?.attachment?.name}
                                                            size={resize(metadata?.attachment?.size)}
                                                            isImage={attachmentIsImage}
                                                            isScanResult={metadata?.attachment?.requestType ? true : false}
                                                            versionId={metadata?.attachment?.versionId || ""}
                                                        />
                                                    </div>
                                                )}
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: message.content }}
                                                />
                                                {metadata?.meeting?.meetingId && (
                                                    <a
                                                        className={Styles["link-href"]}
                                                        href={
                                                            metadata?.meeting?.pathRoute +
                                                            metadata?.meeting.meetingId +
                                                            `/?user_id=${userId}` +
                                                            `${metadata?.onStartVoiceCall ? "&mtype=voice" : ""}`
                                                        }
                                                        target={"_blank"}
                                                    >
                                                        Link
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        {
                                            createdTimestamp && (<div className={Styles["message-time-right"]}>{createdTimestamp}</div>)
                                        }
                                    </div>

                                </div>
                            )
                        }
                        {
                            openEdit && (
                                <div className={Styles.input_zone}>
                                    <textarea
                                        className={Styles["text_area"]}
                                        placeholder={t("Component_Input_Update_Label")}
                                        onChange={userInputChangeHandler}
                                        value={userInput}
                                        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                insertLineBreak();
                                            }
                                        }}
                                        ref={inputRef}
                                    />
                                    <div className={Styles["actions-right"]}>

                                        <div className={(countWordMesssage >= maxSizeMessageInWord) ? `${Styles["input-text-count"]} ${Styles["limited"]}` : `${Styles["input-text-count"]}`}>
                                            <p>{`${countWordMesssage}/${maxSizeMessageInWord}`}</p>
                                        </div>

                                        <div className={Styles["line-style"]}>
                                            <div className={Styles["line-style-line"]}>

                                            </div>
                                        </div>

                                        <div className={`${Styles["actions-right-icon"]}`}>
                                            <Boschicon name="bosch-ic-emoji-happy" onClick={() => setOpenEmojiModal(true)} />
                                        </div>

                                        <div className={Styles["line-style"]}>
                                            <div className={Styles["line-style-line"]}>

                                            </div>
                                        </div>

                                        <div className={`${Styles["actions-right-icon"]}`}>
                                            <Boschicon name="bosch-close" onClick={() => onCancelChange()} />
                                        </div>
                                        <div className={`${Styles["actions-right-icon"]}`}>
                                            <Boschicon name="bosch-ic-checkmark" onClick={() => { onHandleSave(message) }} />
                                        </div>
                                    </div>
                                </div>

                            )
                        }
                    </div>
                </div>
                {openCancelConfirmModal && (
                    <ConfirmCancelDialog isOpen={openCancelConfirmModal} onDiscard={() => handleOnDiscard()} onCancel={() => setOpenCancelConfirmModal(false)}  />
                )}
                {openEmojiModal && (
                    <div className={Styles.emojiForUpdateModal}>
                        <ModalGetEmoji getEmoji={(e: EmojiClickData) => getEmoji(e)}
                            onClose={() => setOpenEmojiModal(false)}
                            isOpen={openEmojiModal} />
                    </div>

                )}

            </>

        );
    }
);

export default EditMessageArea;
